import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Gift, Sparkles, Tag, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const CampaignPopup = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  // Auto-rotate campaigns every 5 seconds
  useEffect(() => {
    if (campaigns.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % campaigns.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [campaigns.length]);

  const fetchCampaigns = async () => {
    try {
      // Fetch active campaigns (public endpoint)
      const response = await axios.get(`${API}/campaigns/active`);
      if (response.data && response.data.length > 0) {
        setCampaigns(response.data);
        // Check if user has dismissed the popup in this session
        const dismissed = sessionStorage.getItem('campaignPopupDismissed');
        if (!dismissed) {
          setTimeout(() => setIsOpen(true), 2000); // Show after 2 seconds
        }
      }
    } catch (error) {
      console.log('No active campaigns');
    }
  };

  const handleDismiss = () => {
    setIsOpen(false);
    sessionStorage.setItem('campaignPopupDismissed', 'true');
  };

  const handleMinimize = () => {
    setIsMinimized(true);
  };

  const handleExpand = () => {
    setIsMinimized(false);
    setIsOpen(true);
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Kod kopyalandı!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  if (campaigns.length === 0) return null;

  const currentCampaign = campaigns[currentIndex];

  // Minimized floating button
  if (isMinimized || !isOpen) {
    return (
      <button
        onClick={handleExpand}
        className="fixed bottom-6 left-6 z-50 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-all duration-300 animate-bounce"
        title="Kampanyaları Gör"
      >
        <Gift className="w-6 h-6" />
        {campaigns.length > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
            {campaigns.length}
          </span>
        )}
      </button>
    );
  }

  // Full popup
  return (
    <div className="fixed bottom-6 left-6 z-50 animate-in slide-in-from-bottom-5 duration-500">
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden w-80 border border-purple-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-4 text-white relative">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            <span className="font-bold">Özel Kampanya!</span>
          </div>
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            onClick={handleMinimize}
            className="absolute top-2 right-9 p-1 hover:bg-white/20 rounded-full transition-colors text-xs"
            title="Küçült"
          >
            —
          </button>
        </div>

        {/* Campaign Content */}
        <div className="p-4">
          {/* Campaign Code */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 mb-4 text-center">
            <p className="text-sm text-purple-600 mb-2">Kampanya Kodu</p>
            <div className="flex items-center justify-center gap-2">
              <code className="bg-purple-100 text-purple-700 px-4 py-2 rounded-lg font-bold text-xl tracking-wider">
                {currentCampaign.code}
              </code>
              <button
                onClick={() => copyCode(currentCampaign.code)}
                className="p-2 hover:bg-purple-100 rounded-lg transition-colors"
                title="Kopyala"
              >
                {copiedCode === currentCampaign.code ? (
                  <Check className="w-5 h-5 text-green-600" />
                ) : (
                  <Copy className="w-5 h-5 text-purple-600" />
                )}
              </button>
            </div>
          </div>

          {/* Discount Info */}
          <div className="text-center mb-4">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full font-bold text-lg">
              <Tag className="w-5 h-5" />
              {currentCampaign.discount_type === 'percentage' 
                ? `%${currentCampaign.discount_value} İNDİRİM`
                : `${currentCampaign.discount_value} ₺ İNDİRİM`
              }
            </div>
          </div>

          {/* Description */}
          {currentCampaign.description && (
            <p className="text-sm text-slate-600 text-center mb-4">
              {currentCampaign.description}
            </p>
          )}

          {/* Validity */}
          <div className="text-xs text-slate-400 text-center">
            {currentCampaign.valid_until && (
              <span>Son geçerlilik: {new Date(currentCampaign.valid_until).toLocaleDateString('tr-TR')}</span>
            )}
          </div>

          {/* Multiple campaigns indicator */}
          {campaigns.length > 1 && (
            <div className="flex justify-center gap-1 mt-4">
              {campaigns.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    idx === currentIndex ? 'bg-purple-600' : 'bg-slate-300'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer CTA */}
        <div className="bg-slate-50 p-3 text-center">
          <p className="text-xs text-slate-500">
            Rezervasyon yaparken kodu kullanın!
          </p>
        </div>
      </div>
    </div>
  );
};

export default CampaignPopup;
